import {
  Button,
  Card,
  Divider,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import React from "react";
import {
  ContentLayout,
  CustomTable,
  HeaderBreadcrumbs,
  Loader,
} from "../../../shared";
import { Link, useLocation, useParams } from "react-router-dom";
import { MorePopOver } from "../components/MorePopOver";
import { SendEmailsDialog } from "../components";

import { useClientAccountStatementDetails } from "../hooks";
import { formatAmount } from "../../../utils";

export const ClientAccountStatementDetails = () => {
  const { pathname } = useLocation();
  const printRef = React.useRef();
  const { statementId, clientId } = useParams();
  const isClientAccountStatement = Boolean(statementId);
  const isStoreAdmin = pathname.includes("store-admin");

  const {
    state,
    handleDownloadOrPrintStatement,
    handleSubmitTheEmail,
    handleToggleSendEmailModal,
    navigateToTransactionDetails,
    getTransactionDetail,
  } = useClientAccountStatementDetails();

  const { statement, detailsLoading } = state;
  const title =
    statement?.billing_cycle_from + " - " + statement?.billing_cycle_to;

  const columns = React.useMemo(
    () => [
      {
        Header: "TXN ID",
        accessor: "id",
      },
      {
        Header: "Date",
        accessor: "transaction_date",
      },
      {
        Header: "Details",
        accessor: "transaction_type",
        Cell: ({ row: { original } }) => {
          const url = navigateToTransactionDetails(original);
          const transactionType = original.transaction_type.toLowerCase();

          return transactionType === "brought forward" ? (
            transactionType
          ) : (
            <Link
              to={url}
              style={{
                fontSize: "14px",
                color: "rgba(23, 23, 23, 1)",
              }}
            >
              {getTransactionDetail(original)}
            </Link>
          );
        },
      },
      {
        Header: "Note",
        accessor: "notes",
      },
      {
        Header: "Debits",
        accessor: "amount",
        Cell: ({
          row: {
            original: { amount, transaction_type },
          },
        }) => {
          const debitAmount = [
            "purchase",
            "payment reverted",
            "brought forward",
          ].includes(transaction_type.toLowerCase())
            ? `$${amount}`
            : "";

          return debitAmount;
        },
      },
      {
        Header: "Credits",
        accessor: "credits",
        Cell: ({
          row: {
            original: { amount, transaction_type },
          },
        }) => {
          const debitAmount = ["refund", "payment"].includes(
            transaction_type.toLowerCase()
          )
            ? `-$${amount}`
            : "";

          return debitAmount;
        },
      },
      {
        Header: "Running Bal",
        accessor: "running_balance",
        Cell: ({
          row: {
            original: { running_balance },
          },
        }) => formatAmount(running_balance),
      },
    ],
    []
  );

  const breadcrumbs = [
    {
      name: "Dashboard",
      href: isStoreAdmin ? "/store-admin" : "/",
    },
    {
      name: "Client Account",
      href: isStoreAdmin
        ? "/store-admin/client-account-v2"
        : "/client-account-v2",
    },
    {
      name: statement?.client_account?.account_name,
      href: isStoreAdmin
        ? `/store-admin/client-account-v2/details/${clientId}`
        : `/client-account-v2/details/${clientId}`,
    },
    {
      name: detailsLoading ? (
        <Skeleton width="152.9px" height="15.73px" />
      ) : (
        title
      ),
    },
  ];

  return (
    <ContentLayout
      title={detailsLoading ? <Skeleton width="287px" height="30px" /> : title}
      breadcrumbs={<HeaderBreadcrumbs links={breadcrumbs} />}
      rightContent={
        <React.Fragment>
          <Button
            sx={{ width: "40px" }}
            type="button"
            size="small"
            variant="text"
            onClick={() => handleDownloadOrPrintStatement("print")}
            disabled={state.isStatementPrinting}
          >
            <Box
              component="img"
              alt="print_statement"
              src="/icons/ic_print.svg"
            />
          </Button>
          <MorePopOver
            SendStatement={() => handleToggleSendEmailModal(true)}
            DownloadInvoice={handleDownloadOrPrintStatement}
          />
          <SendEmailsDialog
            open={state.isOpenSendDialog}
            key={state.isOpenSendDialog}
            handleClose={() => handleToggleSendEmailModal(false)}
            disableCloseOnBackgroundClick
            helperText="The account statement will be sent to"
            handleFormSubmit={handleSubmitTheEmail}
            submitting={state.isEmailSending}
            defaultEmails={statement?.client_account?.statement_emails}
          />
        </React.Fragment>
      }
    >
      {detailsLoading ? (
        <Stack height="calc(100vh - 200px)" justifyContent="center">
          <Loader />
        </Stack>
      ) : (
        <React.Fragment>
          <Card sx={{ mb: "24px" }} ref={printRef}>
            <Box ref={printRef}>
              <Stack
                sx={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  p: "24px",
                }}
              >
                <Typography
                  variant="h5"
                  fontSize={16}
                  sx={{
                    lineHeight: "19px",
                    fontWeight: 600,
                  }}
                >
                  {isClientAccountStatement
                    ? "Statement"
                    : "On Demand Statement"}
                </Typography>
                <Box>
                  <Typography fontWeight={500} variant="body2">
                    Statement as at:
                  </Typography>
                  <Typography variant="body2" fontWeight={500} textAlign="end">
                    {statement?.date_generated}
                  </Typography>
                </Box>
              </Stack>
              <Divider />
              <Stack
                sx={{
                  flexDirection: "row",
                  p: "16px",
                }}
              >
                <Stack
                  sx={{
                    p: "10px",
                    width: "100%",
                    gap: "10px",
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={700}>
                    Store Details
                  </Typography>
                  <Stack gap="6px">
                    <Link
                      to={`/stores/details/${statement?.store?.store_id}`}
                      style={{
                        fontSize: "14px",
                        color: "rgba(77, 77, 77, 1)",
                        fontWeight: 500,
                      }}
                    >
                      {statement?.store?.name}
                    </Link>
                    <Typography variant="body2">
                      ABN: {statement?.store?.abn}
                    </Typography>
                    <Typography variant="body2">
                      Address: {statement?.store?.store_address}
                    </Typography>
                    <Typography variant="body2">
                      {statement?.store?.store_email}
                    </Typography>
                    <Typography variant="body2">
                      {statement?.store?.store_phone}
                    </Typography>
                  </Stack>
                </Stack>
                <Stack
                  sx={{
                    p: "10px",
                    width: "100%",
                    gap: "10px",
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={700}>
                    Bill To
                  </Typography>
                  <Stack
                    sx={{
                      gap: "6px",
                    }}
                  >
                    <Link
                      to={`/client-account-v2/details/${statement?.client_account?.client_account_id}`}
                      style={{
                        fontSize: "14px",
                        color: "rgba(77, 77, 77, 1)",
                        fontWeight: 500,
                      }}
                    >
                      {statement?.client_account?.account_name}
                    </Link>
                    <Typography variant="body2">
                      Account No: {statement?.client_account?.number}
                    </Typography>
                    <Typography variant="body2">
                      ABN: {statement?.client_account?.abn_number}
                    </Typography>
                    <Typography variant="body2">
                      Address: {statement?.client_account?.bill_to_address}
                    </Typography>
                    <Typography variant="body2">
                      {statement?.client_account?.bill_to_email}
                    </Typography>
                    <Typography variant="body2">
                      {statement?.client_account?.bill_to_phone}
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>
            </Box>
          </Card>

          <Stack
            sx={{
              flexDirection: "row",
              gap: "16px",
              marginBottom: "24px",
            }}
          >
            {isClientAccountStatement && (
              <Card
                sx={{
                  p: "20px 24px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  width: "100%",
                }}
              >
                <Typography variant="subtitle2" fontWeight={600}>
                  Brought Forward
                </Typography>
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  color="#636363"
                >
                  {formatAmount(statement?.brought_forward)}
                </Typography>
              </Card>
            )}

            {/*Purchases */}
            <Card
              sx={{
                p: "20px 24px",
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                width: "100%",
              }}
            >
              <Typography variant="subtitle2" fontWeight={600}>
                Purchases
              </Typography>
              <Typography variant="subtitle2" fontWeight={600} color="#636363">
                {formatAmount(statement?.purchases)}
              </Typography>
            </Card>

            {/* Payments */}
            <Card
              sx={{
                p: "20px 24px",
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                width: "100%",
              }}
            >
              <Typography variant="subtitle2" fontWeight={600}>
                Payments
              </Typography>
              <Typography variant="subtitle2" fontWeight={600} color="#636363">
                {formatAmount(statement?.payments)}
              </Typography>
            </Card>

            {isClientAccountStatement && (
              <Card
                sx={{
                  p: "20px 24px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  width: "100%",
                }}
              >
                <Typography variant="subtitle2" fontWeight={600}>
                  Total Due
                </Typography>
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  color="#C07302"
                >
                  {formatAmount(statement?.total_due)}
                </Typography>
              </Card>
            )}

            {isClientAccountStatement && (
              <Card
                sx={{
                  p: "20px 24px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  width: "100%",
                }}
              >
                <Typography variant="subtitle2" fontWeight={600}>
                  Due Date
                </Typography>
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  color="#636363"
                >
                  {statement?.due_date}
                </Typography>
              </Card>
            )}
          </Stack>
          <CustomTable
            tableTitle="Transactions"
            columns={columns}
            data={statement?.transactions ?? []}
            defaultSearch={false}
            searchBar={false}
            pagination={false}
          />
        </React.Fragment>
      )}
    </ContentLayout>
  );
};
